import { computed, effect, inject, untracked } from '@angular/core';
import { patchState, signalStoreFeature, type, withComputed, withHooks, withMethods } from '@ngrx/signals';
import { addEntities, setAllEntities, withEntities } from '@ngrx/signals/entities';
import { AdvancedCommerceStore } from '../advanced-commerce-store.namespace';
import { AdvancedCommerceStoreService } from '../advanced-commerce-store.service';

export function withItems() {
    return signalStoreFeature(
        {
            state: type<AdvancedCommerceStore.State & AdvancedCommerceStore.Meta.State & AdvancedCommerceStore.Response.State>(),
            methods: type<{ metaIsPagingRequestSet(value: boolean): void }>()
        },
        withEntities({ entity: type<AdvancedCommerceStore.Items.Item>(), collection: 'categories' }),
        withEntities({ entity: type<AdvancedCommerceStore.Items.Item>(), collection: 'items' }),
        withEntities({ entity: type<AdvancedCommerceStore.Items.Item>(), collection: 'pages' }),
        withMethods(store => {
            return {
                setAllItems(items: AdvancedCommerceStore.Items.Item[]) {
                    patchState(store, setAllEntities(items, { collection: 'items' }));
                },
                addItems(items: AdvancedCommerceStore.Items.Item[]) {
                    patchState(store, addEntities(items, { collection: 'items' }));
                },
                setAllCategories(items: AdvancedCommerceStore.Items.Item[]) {
                    patchState(store, setAllEntities(items, { collection: 'categories' }));
                },
                setAllPages(items: AdvancedCommerceStore.Items.Item[]) {
                    patchState(store, setAllEntities(items, { collection: 'pages' }));
                }
            };
        }),
        withComputed(store => {
            return {
                hasCategories: computed(() => store.categoriesEntities().length > 0),
                hasItems: computed(() => store.itemsEntities().length > 0),
                hasPages: computed(() => store.pagesEntities().length > 0),
                total: computed(() => store.itemsEntities().length),
                totalAdverts: computed(() => store.itemsEntities().filter(item => item.type === 'advert').length),
                totalCategories: computed(() => store.categoriesEntities().length),
                totalPages: computed(() => store.pagesEntities().length),
                totalProducts: computed(() => store.itemsEntities().filter(item => item.type === 'product').length)
            };
        }),
        withHooks(store => {
            const advancedCommerceStoreService = inject(AdvancedCommerceStoreService);

            return {
                onInit() {
                    effect(() => {
                        const response = store.response();
                        const element = store.element();

                        if (response && element) {
                            const items = advancedCommerceStoreService.getItems('products', response, element);

                            untracked(() => {
                                if (store.metaIsPagingRequest()) {
                                    store.addItems(items);
                                } else {
                                    store.setAllItems(items);
                                }

                                // reset paging request state
                                store.metaIsPagingRequestSet(false);
                            });

                            const categories = advancedCommerceStoreService.getItems('categories', response, element);

                            untracked(() => {
                                store.setAllCategories(categories);
                            });

                            const pages = advancedCommerceStoreService.getItems('pages', response, element);

                            untracked(() => {
                                store.setAllPages(pages);
                            });
                        }
                    });
                }
            };
        })
    );
}
